import { InputHTMLAttributes } from 'react';
import styled from 'styled-components';
import { color, spacing, typography } from '~/theme';

interface TextFieldProps<T> extends InputHTMLAttributes<T> {
    $errors?: boolean | undefined;
    $warnings?: boolean | undefined;
}

export const FormTextField = styled.input<TextFieldProps<HTMLInputElement>>`
    border: 2px solid
        ${props =>
            props?.$errors
                ? color.palette.systemRed
                : props?.$warnings
                  ? color.palette.systemWarningYellow
                  : color.palette.black};
    border-radius: 4px;
    box-sizing: border-box;
    color: ${props => (props.readOnly ? color.palette.greyscale40 : color.palette.black)};
    display: flex;
    font-size: ${typography.defaultFontSize};
    font-family: ${typography.fontPrimary};
    line-height: ${typography.lineHeight17};
    margin: ${spacing.spacing0} ${spacing.spacing0} ${spacing.spacing7};
    padding: 0.625rem ${spacing.spacing4};
    transition: box-shadow 0.1s ease-in-out;
    width: 100%;
    max-width: ${props => {
        switch (props.width) {
            case '30-char-width':
                return '54ex';
            case '20-char-width':
                return '41ex';
            case '10-char-width':
                return '23ex';
            case '7-char-width':
                return '17ex';
            case '5-char-width':
                return '14ex';
            case '4-char-width':
                return '12ex';
            case '3-char-width':
                return '10ex';
            case '2-char-width':
                return '8ex';
            default:
                return '100%';
        }
    }};

    &:focus,
    &:active {
        box-shadow: 0 0 0 4px ${color.focusBlue};
        outline: 4px solid transparent;
        transition: box-shadow 0.1s ease-in-out;
    }
`;

export const ConditionalTextFieldContainer = styled.div`
    margin-top: ${spacing.spacing0};
    margin-left: ${spacing.spacing4};

    & label {
        display: none;
    }

    & FormTextField {
        margin-bottom: calc(${spacing.spacing6} + ${spacing.spacing1});
    }
`;

export const TextFieldWithButtonContainer = styled.div<TextFieldProps<HTMLElement>>`
    position: relative;
    width: 100%;
    max-width: ${props => {
        switch (props.width) {
            case '30-char-width':
                return '54ex';
            case '20-char-width':
                return '41ex';
            case '10-char-width':
                return '23ex';
            case '7-char-width':
                return '17ex';
            case '5-char-width':
                return '14ex';
            case '4-char-width':
                return '12ex';
            case '3-char-width':
                return '10ex';
            case '2-char-width':
                return '8ex';
            default:
                return '100%';
        }
    }};
`;

export const ClearButton = styled.button`
    position: absolute;
    right: ${spacing.spacing2};
    top: ${spacing.spacing2};
    bottom: 0;
    justify-content: center;
    align-items: center;
    width: ${spacing.spacing4};
    height: ${spacing.spacing4};
    appearance: none;
    border: none;
    border-radius: 50%;
    background: transparent;
    margin: 0;
    padding: ${spacing.spacing1};
    color: ${color.palette.black};
    font-size: ${typography.defaultFontSize};
    font-weight: bold;
    cursor: pointer;
`;

export default {
    FormTextField,
    ConditionalTextFieldContainer,
    TextFieldWithButtonContainer,
    ClearButton,
};
